import { GlobalLogger } from "../scanview/debugLogger";
import { InternalOptions } from "../types/scanview.types";
import { State } from "xstate";
import {
  ScanViewContext,
  ScanViewEvent,
} from "../scanview/scanViewStateMachine";

// Default URLs for front and back cards
const DEFAULT_FRONT_CARD_URL =
  "https://cardscan-assets.s3.amazonaws.com/testing/uhc_front.png";
const DEFAULT_BACK_CARD_URL =
  "https://cardscan-assets.s3.amazonaws.com/testing/uhc_back.png";

export interface MockVideoResult {
  stream: MediaStream;
  metadata: { [key: string]: any };
}

export const createMockVideoStream = async (
  stateMachineState: State<ScanViewContext, ScanViewEvent>,
  internalOptions: InternalOptions,
  size = { width: 1920, height: 1080 }
): Promise<MediaStream> => {
  try {
    GlobalLogger.debug("Creating mock video stream", "verbose");

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get 2D context from canvas");
    }

    // Detect if we're on a mobile viewport and adjust canvas size
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobileViewport = viewportWidth < 768; // Standard mobile breakpoint

    if (isMobileViewport && !internalOptions.mockWebcamSize) {
      // For mobile viewports, use a larger canvas for better ML detection
      // The display will be constrained by CSS but the source quality matters
      const mobileWidth = 1280; // Use 720p resolution for mobile
      const mobileHeight = 720;

      canvas.width = mobileWidth;
      canvas.height = mobileHeight;

      GlobalLogger.debug(
        `Mobile viewport detected (${viewportWidth}x${viewportHeight}), using HD canvas size: ${mobileWidth}x${mobileHeight}`,
        "verbose"
      );
    } else {
      canvas.width = size.width;
      canvas.height = size.height;
    }

    // Determine which image to use based on scanning state
    const isBacksideScanning =
      stateMachineState.matches("backsidePending") ||
      stateMachineState.matches("backsideProcessing");

    let imageUrl: string;
    if (internalOptions.mockWebcamFrontUrl && !isBacksideScanning) {
      imageUrl = internalOptions.mockWebcamFrontUrl;
    } else if (internalOptions.mockWebcamBackUrl && isBacksideScanning) {
      imageUrl = internalOptions.mockWebcamBackUrl;
    } else if (isBacksideScanning) {
      imageUrl = DEFAULT_BACK_CARD_URL;
    } else {
      imageUrl = DEFAULT_FRONT_CARD_URL;
    }

    GlobalLogger.debug(`Loading mock card image: ${imageUrl}`, "verbose");

    const img = new Image();
    img.crossOrigin = "anonymous"; // Enable CORS for remote images

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () =>
        reject(new Error(`Failed to load mock image from ${imageUrl}`));
      img.src = imageUrl;
    });

    // Calculate proportional scaling with letterboxing to handle size mismatches
    const imageAspect = img.width / img.height;
    const canvasAspect = canvas.width / canvas.height;

    let drawWidth: number,
      drawHeight: number,
      offsetX = 0,
      offsetY = 0;

    if (imageAspect > canvasAspect) {
      // Image is wider - fit to width, letterbox top/bottom
      drawWidth = canvas.width;
      drawHeight = canvas.width / imageAspect;
      offsetY = (canvas.height - drawHeight) / 2;
    } else {
      // Image is taller - fit to height, letterbox left/right
      drawHeight = canvas.height;
      drawWidth = canvas.height * imageAspect;
      offsetX = (canvas.width - drawWidth) / 2;
    }

    // Fill background (letterbox areas) with dark color to match camera background
    ctx.fillStyle = "#2f4f4f";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw scaled card image
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

    GlobalLogger.debug(
      `Mock video stream created: ${canvas.width}x${canvas.height}, image: ${img.width}x${img.height}`,
      "verbose"
    );

    // Create stream from canvas
    const stream = canvas.captureStream(30); // 30 FPS

    // Add mock video metadata to match real camera metadata
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      Object.defineProperty(videoTrack, "getSettings", {
        value: () => ({
          width: canvas.width,
          height: canvas.height,
          deviceId: "mock-camera-device",
          groupId: "mock-camera-group",
          facingMode: "environment",
          frameRate: 30,
        }),
        writable: false,
      });
    }

    return stream;
  } catch (error) {
    GlobalLogger.error(`Failed to create mock video stream: ${error}`);
    throw error;
  }
};

export const setupMockVideo = async (
  stateMachineState: State<ScanViewContext, ScanViewEvent>,
  internalOptions: InternalOptions,
  size = { width: 1920, height: 1080 }
): Promise<MockVideoResult> => {
  try {
    GlobalLogger.debug("Setting up mock video with metadata", "verbose");

    const stream = await createMockVideoStream(
      stateMachineState,
      internalOptions,
      size
    );

    // Create comprehensive mock video metadata that matches real camera metadata
    const videoTrack = stream.getVideoTracks()[0];
    const settings = videoTrack?.getSettings() || {};

    const metadata: { [key: string]: any } = {
      videoTrack: settings,
      cameraDeviceInfo: {
        deviceId: "mock-camera-device",
        groupId: "mock-camera-group",
        kind: "videoinput",
        label: "Mock Camera",
      },
      cameraCapabilities: {
        width: { min: 640, max: size.width },
        height: { min: 480, max: size.height },
        frameRate: { min: 15, max: 30 },
      },
    };

    GlobalLogger.debug("Mock video setup completed with metadata", "verbose");

    return { stream, metadata };
  } catch (error) {
    GlobalLogger.error(`Failed to setup mock video: ${error}`);
    throw error;
  }
};
